import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { motion } from 'framer-motion';
import { SearchEnrichmentInsight, SearchEnrichmentDebugInfo } from '../../types';
import SearchEnrichmentDevDisplay from './SearchEnrichmentDevDisplay';

// Check if we're in dev mode (based on env variable set in .env.dev vs .env.production)
const IS_DEV_MODE = process.env.REACT_APP_DEV_MODE === 'true';

// Debug logging for dev mode
console.log('[SearchEnrichmentDisplay] REACT_APP_DEV_MODE:', process.env.REACT_APP_DEV_MODE);
console.log('[SearchEnrichmentDisplay] IS_DEV_MODE:', IS_DEV_MODE);

interface SearchEnrichmentDisplayProps {
  insights: SearchEnrichmentInsight[];
  debugInfo?: SearchEnrichmentDebugInfo;
}

const SearchEnrichmentDisplay: React.FC<SearchEnrichmentDisplayProps> = ({ insights, debugInfo }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  
  // Debug logging for props - more detailed
  console.log('[SearchEnrichmentDisplay] Props:', {
    insightsCount: insights?.length,
    hasDebugInfo: !!debugInfo,
    debugInfoKeys: debugInfo ? Object.keys(debugInfo) : [],
    searchQueriesCount: debugInfo?.search_queries?.length,
    searchResultsCount: debugInfo?.search_results?.length,
    researcherModel: debugInfo?.researcher_model,
    reporterModel: debugInfo?.reporter_model
  });
  
  if (!insights || insights.length === 0) {
    return null;
  }

  // In dev mode, use the dev display component with full debug info
  if (IS_DEV_MODE) {
    return <SearchEnrichmentDevDisplay insights={insights} debugInfo={debugInfo} />;
  }

  // Production display - simpler, cleaner view
  // Separate insights by type
  const actionInsights = insights.filter(i => i.type === 'action');
  const clarificationInsights = insights.filter(i => i.type === 'clarification');

  return (
    <div className="mt-6 rounded-xl overflow-hidden border-2 border-dashed border-primary/30 dark:border-primary/40 bg-gradient-to-br from-primary/5 to-transparent dark:from-primary/10">
      {/* Header */}
      <div 
        className="flex justify-between items-center px-4 py-3 bg-primary/10 dark:bg-primary/20 cursor-pointer hover:bg-primary/15 dark:hover:bg-primary/25 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center space-x-2">
          <svg 
            className="w-5 h-5 text-primary" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
            />
          </svg>
          <h3 className="text-sm font-semibold text-primary dark:text-primary">
            Additional Insights from Web Research
          </h3>
          <span className="text-xs px-2 py-0.5 rounded-full bg-primary/20 text-primary font-medium">
            {insights.length} insight{insights.length !== 1 ? 's' : ''}
          </span>
        </div>
        <button 
          className="p-1 hover:bg-primary/20 rounded transition-colors"
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
        >
          <svg 
            className={`w-5 h-5 text-primary transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="px-4 py-4 space-y-5">
          {/* Action Insights */}
          {actionInsights.length > 0 && (
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-text-primary dark:text-dark-textPrimary flex items-center space-x-2">
                <span className="flex items-center justify-center w-6 h-6 rounded-lg bg-gradient-to-br from-primary to-primary-dark text-white shadow-md shadow-primary/30">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </span>
                <span>Actionable Recommendations</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary-light font-medium">
                  {actionInsights.length}
                </span>
              </h4>
              <div className="space-y-4">
                {actionInsights.map((insight, idx) => (
                  <InsightCard key={insight.id} insight={insight} variant="action" index={idx} />
                ))}
              </div>
            </div>
          )}

          {/* Clarification Insights */}
          {clarificationInsights.length > 0 && (
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-text-primary dark:text-dark-textPrimary flex items-center space-x-2">
                <span className="flex items-center justify-center w-6 h-6 rounded-lg bg-gradient-to-br from-primary-light to-primary text-white shadow-md shadow-primary/20">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </span>
                <span>Contextual Information</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-primary/5 dark:bg-primary/15 text-primary/80 dark:text-primary-light/80 font-medium">
                  {clarificationInsights.length}
                </span>
              </h4>
              <div className="space-y-4">
                {clarificationInsights.map((insight, idx) => (
                  <InsightCard key={insight.id} insight={insight} variant="clarification" index={idx} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface InsightCardProps {
  insight: SearchEnrichmentInsight;
  variant: 'action' | 'clarification';
  index: number;
}

// Modern Insight Card with 2025 AI UX - using app's design system glow utilities
const InsightCard: React.FC<InsightCardProps> = ({ insight, variant, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Copy to clipboard handler
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(insight.content);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  // Extract domain from URL for favicon
  const getDomain = (url: string) => {
    try {
      return new URL(url).hostname;
    } catch {
      return '';
    }
  };
  
  // Check if content is long (more than ~200 chars)
  const isLongContent = insight.content.length > 200;

  // Color scheme based on variant - using primary shades for action, teal/cyan for context
  const colorScheme = variant === 'action' 
    ? {
        bgGradient: 'from-primary-500/5 via-primary-400/3 to-transparent',
        leftBar: 'from-primary-500 to-primary-700',
        badge: 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300',
        badgeIcon: 'from-primary-400 to-primary-600',
        hoverText: 'text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300',
      }
    : {
        bgGradient: 'from-cyan-500/5 via-teal-400/3 to-transparent',
        leftBar: 'from-cyan-400 to-teal-600',
        badge: 'bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-300',
        badgeIcon: 'from-cyan-400 to-teal-500',
        hoverText: 'text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300',
      };

  return (
    <div 
      className={`group relative rounded-xl overflow-hidden transition-all duration-300 ease-out
        bg-white dark:bg-dark-secondary/90 border border-neutral-dark dark:border-dark-border
        hover-glow-lg halo-container
        ${isHovered ? 'shadow-glow-md' : 'shadow-sm'}
        hover:-translate-y-0.5`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Subtle gradient overlay with color scheme */}
      <div className={`absolute inset-0 opacity-[0.03] dark:opacity-[0.06] pointer-events-none
        bg-gradient-to-br ${colorScheme.bgGradient}`} 
      />
      
      {/* Left accent bar with color scheme */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 
        bg-gradient-to-b ${colorScheme.leftBar}`} 
      />
      
      {/* Content */}
      <div className="relative px-5 py-4 pl-6">
        {/* Top row: Number badge + Action buttons */}
        <div className="flex items-start justify-between mb-3">
          {/* Insight number with glow effect */}
          <div className="flex items-center space-x-2">
            <span className={`flex-shrink-0 w-7 h-7 rounded-lg 
              bg-gradient-to-br ${colorScheme.badgeIcon}
              text-white flex items-center justify-center text-xs font-bold
              shadow-md ${variant === 'action' ? 'shadow-primary/30' : 'shadow-cyan-500/30'}`}>
              {index + 1}
            </span>
            {/* Type badge */}
            <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full
              ${colorScheme.badge}`}>
              {variant === 'action' ? '⚡ Action' : '💡 Context'}
            </span>
          </div>
          
          {/* Hover action buttons */}
          <div className={`flex items-center space-x-1 transition-all duration-200 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
            <button
              onClick={handleCopy}
              className={`p-1.5 rounded-lg transition-all duration-200
                ${isCopied 
                  ? (variant === 'action' 
                    ? 'bg-primary/20 dark:bg-primary/30 text-primary dark:text-primary-light'
                    : 'bg-cyan-500/20 dark:bg-cyan-500/30 text-cyan-600 dark:text-cyan-400')
                  : 'hover:bg-neutral dark:hover:bg-dark-border/50 text-text-tertiary dark:text-dark-textTertiary hover:text-primary'}`}
              title={isCopied ? 'Copied!' : 'Copy insight'}
            >
              {isCopied ? (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              )}
            </button>
          </div>
        </div>
        
        {/* Main content - simple content swap without broken height animation */}
        <div className="prose dark:prose-invert prose-neutral max-w-none 
          prose-p:text-[15px] prose-p:leading-relaxed prose-p:text-text-primary prose-p:dark:text-dark-textPrimary prose-p:my-1
          prose-strong:text-text-primary prose-strong:dark:text-white prose-strong:font-semibold
          prose-a:text-primary prose-a:no-underline hover:prose-a:underline">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {isLongContent && !isExpanded 
              ? insight.content.slice(0, 180) + '...' 
              : insight.content}
          </ReactMarkdown>
        </div>
        
        {/* Expand/Collapse for long content */}
        {isLongContent && (
          <motion.button
            onClick={() => setIsExpanded(!isExpanded)}
            className={`mt-2 text-xs font-medium ${colorScheme.hoverText}
              flex items-center space-x-1 transition-colors`}
            whileHover={{ x: 2 }}
            whileTap={{ scale: 0.98 }}
          >
            <span>{isExpanded ? 'Show less' : 'Read more'}</span>
            <motion.svg 
              className="w-3 h-3"
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.2 }}
              fill="none" viewBox="0 0 24 24" stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </motion.svg>
          </motion.button>
        )}
        
        {/* Source with favicon and better styling */}
        {insight.source && (
          <div className="mt-4 pt-3 border-t border-neutral-dark/30 dark:border-dark-border/30">
            <a 
              href={insight.source.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 text-xs text-text-secondary dark:text-dark-textSecondary 
                hover:text-primary dark:hover:text-primary transition-all duration-200 group/link
                px-2.5 py-1.5 -mx-2.5 rounded-lg hover:bg-neutral/80 dark:hover:bg-dark-border/30"
            >
              {/* Favicon */}
              <img 
                src={`https://www.google.com/s2/favicons?domain=${getDomain(insight.source.url)}&sz=16`}
                alt=""
                className="w-4 h-4 rounded-sm"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
              <span className="truncate max-w-sm group-hover/link:text-primary transition-colors" title={insight.source.title}>
                {insight.source.title}
              </span>
              <svg className="w-3 h-3 flex-shrink-0 opacity-50 group-hover/link:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchEnrichmentDisplay;
